/**
 * CallBoardNode Types - Board composition node
 *
 * Allows one board to call another board as a tool/function.
 * The called board executes and returns its output data.
 *
 * This enables board composition - complex workflows can call
 * simpler boards as subroutines, similar to function calls in code.
 *
 * Example usage:
 * - A "Customer Support" board calls a "Sentiment Analysis" board
 * - A "Report Generator" board calls multiple data processing boards
 */

// ============================================================================
// STATUS TYPES
// ============================================================================

/**
 * Status of the board call execution.
 *
 * State machine:
 *   idle -> calling -> complete
 *         \        -> error
 *
 * - idle: No call in progress, ready to execute
 * - calling: Currently executing the target board
 * - complete: Execution finished successfully
 * - error: Execution failed with an error
 */
export type CallBoardStatus = 'idle' | 'calling' | 'complete' | 'error';

// ============================================================================
// NODE DATA
// ============================================================================

/**
 * Data stored in a CallBoardNode.
 *
 * CallBoardNode enables board composition by letting one board
 * call another as a tool. The target board executes with mapped
 * inputs and returns its output data.
 *
 * Key concepts:
 * - targetBoardId: Which board to call
 * - inputMapping: How to pass data to the target board
 * - versionPin: Lock to specific board version (like npm versioning)
 * - result: Output data from the target board
 */
export interface CallBoardNodeData {
  /** Index signature for React Flow compatibility */
  [key: string]: unknown;

  /** Display title for the node (user-editable) */
  title: string;

  // ========================================
  // Target Board Configuration
  // ========================================

  /** ID of the board to call (undefined if not yet selected) */
  targetBoardId?: string;

  /** Display name of the target board (for UI) */
  targetBoardName?: string;

  /**
   * Pinned version string (like npm semver).
   * If set, always uses this specific version of the board.
   * If undefined, uses the latest/active version.
   *
   * Example: "1.2.3" or "^1.0.0"
   */
  versionPin?: string;

  // ========================================
  // Input Mapping
  // ========================================

  /**
   * Maps target board inputs to source expressions.
   *
   * Keys are the target board's input port names.
   * Values are expressions that resolve to the input value.
   *
   * Expression examples:
   * - "{{input.text}}" - Reference incoming connection
   * - "static value" - Hardcoded string
   * - "{{nodes.xyz.output}}" - Reference another node's output
   *
   * Example:
   * {
   *   "query": "{{input.userQuestion}}",
   *   "language": "en"
   * }
   */
  inputMapping: Record<string, string>;

  // ========================================
  // Execution State
  // ========================================

  /** Current execution status */
  status: CallBoardStatus;

  /**
   * Result data from the target board execution.
   * Structure depends on the target board's output schema.
   * Only set when status is 'complete'.
   */
  result?: unknown;

  /**
   * Error message if execution failed.
   * Only set when status is 'error'.
   */
  error?: string;

  /**
   * ISO timestamp of last successful call.
   * Useful for caching and debugging.
   */
  lastCalledAt?: string;

  /**
   * Duration of the last call in milliseconds.
   * Helps users understand performance.
   */
  durationMs?: number;

  // ========================================
  // Callbacks (set by React Flow wrapper)
  // ========================================

  /** Called when user edits the title */
  onTitleChange?: (title: string) => void;

  /** Called when user selects a different target board */
  onTargetBoardChange?: (boardId: string, boardName: string) => void;

  /** Called when user modifies input mapping */
  onInputMappingChange?: (mapping: Record<string, string>) => void;

  /** Called when user clicks the "Call Board" button */
  onCall?: () => void;

  // ========================================
  // Runtime Context (injected by canvas)
  // ========================================

  /** Yjs document for real-time sync */
  ydoc?: unknown;

  /** ID of the board containing this node */
  boardId?: string;

  /** Yjs map of all nodes (for cross-node references) */
  nodesMap?: unknown;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Creates default data for a new CallBoardNode
 */
export function createDefaultCallBoardNodeData(): CallBoardNodeData {
  return {
    title: 'Call Board',
    inputMapping: {},
    status: 'idle',
  };
}
